import type { ConversationMessage, ConversationTurn } from "./conversationSchemas";

export type ConversationTurnReconciliationDecision =
  | "complete"
  | "continue"
  | "terminal";

/** 只清理由同一发送触发的追赶，避免迟到 transport 错误误伤后续轮次。 */
export function clearConversationReconciliationTurn(
  currentTurnId: string,
  failedTurnId: string,
) {
  return currentTurnId === failedTurnId ? "" : currentTurnId;
}

export function conversationTurnReconciliationDecision(input: {
  messages: ReadonlyArray<Pick<ConversationMessage, "id">>;
  turnId: string;
  turns: ReadonlyArray<ConversationTurn>;
}): ConversationTurnReconciliationDecision {
  const turn = input.turns.find(
    (candidate) =>
      candidate.turnId === input.turnId ||
      candidate.userMessageId === input.turnId,
  );
  if (!turn || turn.status === "pending" || turn.status === "streaming") {
    return "continue";
  }
  if (turn.status === "failed" || turn.status === "stopped") {
    return "terminal";
  }
  return turn.latestAssistantMessageId &&
    input.messages.some((message) => message.id === turn.latestAssistantMessageId)
    ? "complete"
    : "continue";
}

export interface ConversationTurnReconciliationRead {
  abort(): void;
  settled: Promise<{ continuePolling: boolean }>;
}

/** 串行追赶当前轮次；只调 GET，后台暂停并在恢复时立即重校准。 */
export class ConversationTurnReconciliationScheduler {
  private readonly startedAt: number;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private task: ConversationTurnReconciliationRead | null = null;
  private paused = false;
  private disposed = false;
  private requested = false;

  constructor(
    private readonly read: () => ConversationTurnReconciliationRead,
    private readonly onTimeout: () => void,
    private readonly now: () => number = Date.now,
    private readonly intervalMs = 1_500,
    private readonly timeoutMs = 300_000,
  ) {
    this.startedAt = now();
  }

  requestNow() {
    if (this.disposed) return;
    this.clearTimer();
    this.requested = true;
    this.start();
  }

  pause() {
    this.paused = true;
    this.clearTimer();
    this.task?.abort();
  }

  resume() {
    this.paused = false;
    this.requestNow();
  }

  dispose() {
    this.disposed = true;
    this.clearTimer();
    this.task?.abort();
  }

  private clearTimer() {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  private start() {
    if (this.disposed || this.paused || this.task || !this.requested) return;
    if (this.now() - this.startedAt >= this.timeoutMs) {
      this.requested = false;
      this.onTimeout();
      return;
    }
    this.requested = false;
    const task = this.read();
    this.task = task;
    void task.settled
      .catch(() => ({ continuePolling: true }))
      .then((result) => {
        this.task = null;
        if (this.disposed || this.paused) return;
        if (this.requested) {
          this.start();
          return;
        }
        if (!result.continuePolling) return;
        const remaining = this.timeoutMs - (this.now() - this.startedAt);
        if (remaining <= 0) {
          this.onTimeout();
          return;
        }
        this.timer = setTimeout(
          () => this.requestNow(),
          Math.min(this.intervalMs, remaining),
        );
      });
  }
}
